import {isNil} from 'ramda';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {Icon} from 'semantic-ui-react';
import styled, {css} from 'styled-components';
import {loadActivities, retrieveUser} from '../../api';
import FloatingActionButton from '../../components/Button/FloatingActionButton';
import MenuButton from '../../components/Button/MenuButton';
import Card from '../../components/Card/Card';
import {activityScoringMetadata} from '../../util/ActivityMetadata';
import {loadUser} from '../../util/Session';
import {currencyFormatter} from '../../util/Utils';
import {backgroundColorFromTotal, BRIGHT_RED, ColoredContainer, DARK_GREEN, LIGHT_GREEN, LIGHT_RED, YELLOW} from '../../components/Container/ContainerMix';

const DashboardHeader = styled(ColoredContainer)`
  min-height: 350px;
  color: ${props => props.hasLoggedActivityToday ? '#FFFFFF' : '#8C8C8C'};
  justify-content: start;
  text-align: center;
  & > h3 {
    font-size: 18px;
  }
  & > p {
    font-size: 5.5rem !important;
    margin: 0 0 10px 0;
    text-shadow: 0px 3px rgba(0,0,0,0.1) !important;
    font-weight: lighter !important;
    font-family: Roboto, sans-serif;
    @media (max-width: 320px) {
      font-size: 4.5rem !important;
    }
  }
`;

const TableWrapper = styled.div`
  padding: 21px;
  background-color: #f6f6f6
  position: relative;
  padding-bottom: 100px;
`;

const Wrapper = styled.div`
  height: calc(100vh - 86px);
  position: unset;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const TotalsWrapper = styled.div`
  border-radius: 15px;
  background-color: #FFFFFF;
  box-shadow: 0 4px 0 0 rgba(0,0,0,0.2);
  margin-top: -73px;
`;

const Totals = styled.div`
  padding: 16px 20px;
  color: #2E2E33;
  font-family: Roboto;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 1.12px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  &:first-child {
    border-bottom: 1px solid #D8D8D8;
  }
`;

const Total = styled.span`
  color: ${props => props.isNegative ? BRIGHT_RED : DARK_GREEN};
  padding-right: 20px;
`;

const TotalLabel = styled.span`
  flex: 1;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 5px 0;
`;

const ListItemLabel = styled.p`
  margin: 0;
  display: block;
  flex: 1;
  font-size: 12px;
  color: #666666;
  letter-spacing: 0.87px;
  line-height: 16px;
  padding: 0 10px;
`;

const ListItemTotal = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FlexWithPadding = styled(Flex)`
  padding: 0px 10px;
  height: 86px;
  background-color: ${props => backgroundColorFromTotal(props.total, props.hasLoggedActivityToday)};
`;

const Title = styled.h5`
  margin: 0;
  color: rgba(255, 255, 255, 1);
  min-width: 150px;
  font-family: Roboto;
  cursor: pointer;
  padding: 9px 0;
`;

const HeaderNavigation = styled.div`
  cursor: pointer;
  width: 46px !important;
  height: 46px !important;
  box-shadow: 0 2px 2px 0 rgba(0,0,0,0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 1.8em;
  font-weight: 900;
  font-family: Arial;
  color: rgba(0,0,0,.5);
`;

const ActivityLogCount = styled(HeaderNavigation)`
  background-color: transparent;
  border: 3px solid rgb(255, 255, 255);
  color: #ffffff !important;
  ${props => props.hasLoggedAllActivitiesToday && css`
    width: 80px !important;
    height: 80px !important;
    background-image: url(/images/star.svg);
    background-repeat: no-repeat;
    background-size: 75px;
    background-position: center center;
    overflow: visible;
    border: none;
    box-shadow: none;
    margin-right: -10px;
  `}
`;

const Logo = styled.img`
  max-width: 35px;
  max-height: 35px;
  padding: 0 5px 0 0;
`;

const MenuText = styled.span`
  display: flex;
  justify-content: space-around;
`;

const SubHeader = styled.div`
  text-align: center;
`;

const SubTitle = styled.h3`
  padding: 0 0 10px 0;
`;

const Circle = styled.div`
  background-color: ${props => props.backgroundColor};
  border: 1px solid #FFFFFF;
  height: 50px;
  border-radius: 50%;
  width: 50px;
  margin-left: calc(50% - 25px);
`;

class Dashboard extends Component {
  constructor() {
    super();
    this.state = {
      activities: [],
      totalPoints: 0
    };
  }

  componentDidMount() {
    return Promise.all([loadActivities(), this.reloadScore()])
      .then(responses => {
        this.setState({activities: responses[0].data});
      });
  }

  onTitleClick = () => {
    const {router} = this.props;
    router.push('/dashboard');
  };

  getActivityByType = (type) => {
    const {activities} = this.state;
    if (isNil(activities)) return null;
    return activities.find(a => a.activity_type === type);
  };

  getActivityTypeTotal = (type) => {
    const activityType = this.getActivityByType(type);
    return isNil(activityType) ? 0 : activityType.total;
  };

  getActivityTypePropertyTotal = (type, propertyKey) => {
    const activityType = this.getActivityByType(type);
    const properties = isNil(activityType) ? null : activityType.properties;
    const dollars = isNil(properties) ? 0 : properties.find(p => p.property_key === propertyKey).total;
    return currencyFormatter.format(dollars);
  };

  getActivityTypeMax = (type) => {
    const activityType = activityScoringMetadata[type];
    return isNil(activityType) ? 100 : activityType.max;
  };

  getFoodLevelColor = () => {
    const foodTotal = this.getActivityTypeTotal('food');
    if (foodTotal >= 2.5) {
      return DARK_GREEN;
    } else if (foodTotal < 2.5 && foodTotal >= 1) {
      return LIGHT_GREEN;
    } else if (foodTotal < 1 && foodTotal > -1) {
      return YELLOW;
    } else if (foodTotal <= -1 && foodTotal > -2.5) {
      return LIGHT_RED;
    } else {
      return BRIGHT_RED;
    }
  };

  // The fitness max score is a little different because of the piggy bank.
  // We want to show up to 6 so you can tell when you've earned a piggy bank but the max score you can earn per
  // day is really 4. This function gets that value (the max score you can earn per day)
  getFitnessMaxScore = () => {
    const activityType = activityScoringMetadata.fitness;
    return isNil(activityType) ? 100 : activityType.max_score;
  };

  reloadScore = () => {
    const currentUser = loadUser();
    if (isNil(currentUser)) return null;
    return retrieveUser(currentUser.id)
      .then(response => {
        const user = response.data;
        this.setState({totalPoints: user.points});
      });
  };

  render() {
    const {activities, totalPoints} = this.state;
    const {onClickSidebar} = this.props;

    const debits = activities
      .filter(a => a.activity_type !== 'stress')
      .reduce((total, activity) => total + activity.total, 0);
    const stressActivity = activities.find(a => a.activity_type === 'stress') || [];
    const credits = activities
      .filter(a => a.activity_type === 'stress')
      .reduce((total, activity) => Math.abs(total + activity.total), 0);

    const hasLoggedActivityToday = activities.length > 0;
    const hasLoggedAllActivitiesToday = activities.length === 8 && stressActivity.properties.length === 5;
    const total = debits - credits;

    return (
      <div>
        <FlexWithPadding total={total} hasLoggedActivityToday={hasLoggedActivityToday}>
          <MenuButton onClick={onClickSidebar}/>
          <MenuText>
            <Logo src="images/mark-white.svg" alt="Logo" />
            <Title onClick={this.onTitleClick}>My BodyBank Account</Title>
          </MenuText>
          <ActivityLogCount hasLoggedAllActivitiesToday={hasLoggedAllActivitiesToday}>{totalPoints}</ActivityLogCount>
        </FlexWithPadding>
        <Wrapper>
          <FloatingActionButton activities={activities} incompleteActivities={!hasLoggedAllActivitiesToday}/>
          <DashboardHeader total={total} hasLoggedActivityToday={hasLoggedActivityToday}>
            <h3>Today's Balance</h3>
            <p>{currencyFormatter.format(total)}</p>
            <SubHeader>
              <SubTitle>Today's Food Level</SubTitle>
              <Circle backgroundColor={this.getFoodLevelColor()}/>
            </SubHeader>
          </DashboardHeader>
          <TableWrapper>
            <TotalsWrapper>
              <Totals>
                <TotalLabel>Stress</TotalLabel>
                <Total isNegative>-{currencyFormatter.format(credits)}</Total>
              </Totals>
              <Totals>
                <TotalLabel>Energy</TotalLabel>
                <Total isNegative={false}>+{currencyFormatter.format(debits)}</Total>
              </Totals>
            </TotalsWrapper>
            <div>
              <Card
                title="Sleep"
                activityTotal={this.getActivityTypeTotal('sleep')}
                activityMax={this.getActivityTypeMax('sleep')}
                emptyMessage="Try to get 9 hours of sleep!"
              >
                <ListItem>
                  <Icon circular inverted color="blue" name="bed"/>
                  <ListItemLabel>Sleep from last night</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('sleep', 'minutes')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Water"
                activityTotal={this.getActivityTypeTotal('water')}
                activityMax={this.getActivityTypeMax('water')}
                emptyMessage="Get hydrated today!"
              >
                <ListItem>
                  <Icon circular inverted color="blue" name="coffee"/>
                  <ListItemLabel>Cups of water</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('water', 'cups')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon circular inverted color="orange" name="lemon"/>
                  <ListItemLabel>Handfuls of fruit</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('water', 'fruit')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon circular inverted color="red" name="utensils"/>
                  <ListItemLabel>Handfuls of vegetables</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('water', 'vegetables')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Physical Activity"
                activityTotal={this.getActivityTypeTotal('fitness')}
                activityMax={this.getFitnessMaxScore()}
                emptyMessage="Get active today!"
              >
                <ListItem>
                  <Icon circular inverted color="purple" name="heartbeat"/>
                  <ListItemLabel>Vigorous Intensity</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('fitness', 'vigorous_duration')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon circular inverted color="green" name="trophy"/>
                  <ListItemLabel>Moderate Intensity</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('fitness', 'moderate_duration')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Food/Substance"
                activityTotal={this.getActivityTypeTotal('food')}
                activityMax={this.getActivityTypeMax('food')}
                emptyMessage="Think about what you put in your body!"
              >
                <ListItem>
                  <Icon size="large" color="green" name="circle"/>
                  <ListItemLabel>Green level</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('food', 'green')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon size="large" color="yellow" name="circle"/>
                  <ListItemLabel>Yellow level</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('food', 'yellow')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon size="large" color="red" name="circle"/>
                  <ListItemLabel>Red level</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('food', 'red')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Rest/Mindfulness"
                activityTotal={this.getActivityTypeTotal('rest')}
                activityMax={this.getActivityTypeMax('rest')}
                emptyMessage="Rest up!"
              >
                <ListItem>
                  <Icon circular inverted color="purple" name="chart line"/>
                  <ListItemLabel>Taking time to rest</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('rest', 'duration')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Sunshine"
                activityTotal={this.getActivityTypeTotal('sunshine')}
                activityMax={this.getActivityTypeMax('sunshine')}
                emptyMessage="Make sure to soak in some vitamin D!"
              >
                <ListItem>
                  <Icon circular inverted color="yellow" name="sun"/>
                  <ListItemLabel>Soaking up some sun</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('sunshine', 'duration')}</ListItemTotal>
                </ListItem>
              </Card>

              <Card
                title="Hygiene"
                activityTotal={this.getActivityTypeTotal('cleanliness')}
                activityMax={this.getActivityTypeMax('cleanliness')}
                emptyMessage="Freshen up!"
              >
                <ListItem>
                  <Icon circular inverted color="orange" name="shower"/>
                  <ListItemLabel>Showers</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('cleanliness', 'body')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon circular inverted color="blue" name="paint brush"/>
                  <ListItemLabel>Brushing your teeth</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('cleanliness', 'teeth')}</ListItemTotal>
                </ListItem>
                <ListItem>
                  <Icon circular inverted color="yellow" name="first aid"/>
                  <ListItemLabel>Flossing your teeth</ListItemLabel>
                  <ListItemTotal>{this.getActivityTypePropertyTotal('cleanliness', 'floss')}</ListItemTotal>
                </ListItem>
              </Card>

            </div>
          </TableWrapper>
        </Wrapper>
      </div>
    );
  }
}

Dashboard.propTypes = {
  onClickSidebar: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired
};

export default withRouter(Dashboard);



// WEBPACK FOOTER //
// ./src/pages/Dashboard/Dashboard.js